import { useState } from 'react';
import { FileText, Plus, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError, EmptyState } from '@/components/ui/error-boundary';
import type { EmployeeDetail } from '@/types/employee';
import { useDocuments, useDeleteDocument } from '@/hooks/use-documents';
import { DocumentFolderTree } from './document-folder-tree';
import { DocumentUploadDialog } from './document-upload-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';
import type { Document } from '@/types/document';
import {
  useCanEditPaymentDetails,
  useCanViewPaymentDetails,
  usePermission,
} from '@/lib/permissions';
import { extractErrorMessage } from '@/lib/errors';
import { toast } from 'sonner';
import { PaymentDetailsBlock } from './payment-details-block';
import { EducationBlock } from './education-block';

interface DocumentsTabProps {
  employee: EmployeeDetail;
  /**
   * `canViewEmployeeDocuments` — the file tree's gate (HW-785). The tab
   * itself opens for every reader of the card, so without it the tree and the
   * tab's custom blocks are neither requested nor shown. [HW-1008]
   */
  canViewDocuments: boolean;
}

/**
 * The Documents tab: FOP payment details, Education, then — for document
 * readers only — the document files and the tab's custom blocks. [HW-1008]
 */
export function DocumentsTab({ employee, canViewDocuments }: DocumentsTabProps) {
  const canViewPaymentDetails = useCanViewPaymentDetails(employee.id);
  const canEditPaymentDetails = useCanEditPaymentDetails();
  const canViewEducation = usePermission('employees.profile.view');
  const canEditEducation = usePermission('employees.profile.edit');
  // The tab opens for every reader of the card; one who may see none of its
  // blocks gets a line saying so rather than a blank tab.
  if (!canViewPaymentDetails && !canViewEducation && !canViewDocuments) {
    return (
      <EmptyState
        icon={<FileText className="h-10 w-10" />}
        title="Nothing to show"
        description="You don't have access to this employee's documents, payment details or education."
      />
    );
  }
  return (
    <div className="space-y-4">
      <PaymentDetailsBlock
        employeeId={employee.id}
        canView={canViewPaymentDetails}
        canEdit={canEditPaymentDetails}
      />
      {/* GET /education requires employees.profile.view. [HW-1008] */}
      {canViewEducation && <EducationBlock employeeId={employee.id} canEdit={canEditEducation} />}
      {/* Files and the tab's custom blocks keep the documents gate the whole tab
          had before it opened for every reader of the card. [HW-785] */}
      {canViewDocuments && (
        <>
          <DocumentFiles employee={employee} />
          <CustomBlocksSection section="DOCUMENTS" employeeId={employee.id} />
        </>
      )}
    </div>
  );
}

function DocumentFiles({ employee }: { employee: EmployeeDetail }) {
  const { data: documents, isLoading, error } = useDocuments(employee.id);
  const queryClient = useQueryClient();
  const canUpload = usePermission('employees.documents.upload');
  const canDelete = usePermission('employees.documents.delete');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<Document | null>(null);
  const deleteDocument = useDeleteDocument(employee.id);

  const handleConfirmDelete = () => {
    if (!docToDelete) return;
    deleteDocument.mutate(docToDelete.id, {
      onSuccess: () => {
        toast.success('Document successfully deleted');
        setDocToDelete(null);
      },
      onError: (err) => {
        toast.error(extractErrorMessage(err, 'Failed to delete document'));
      },
    });
  };

  const handleDocumentClick = (document: Document) => {
    let url: string | null = null;

    if (document.localFilePath) {
      url = `/api/v1/documents/${document.employeeId}/${document.id}`;
    } else if (document.fileUrl) {
      url = document.fileUrl;
    } else if (document.url) {
      url = document.url;
    }

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Documents
          </h3>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Documents
          </h3>
        </div>
        <div className="p-5">
          <QueryError error={error} />
        </div>
      </div>
    );
  }

  // Use folders directly from API
  const folders = documents || [];
  const hasDocuments = folders.some((folder) => folder.documents.length > 0);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Documents
        </h3>
        {canUpload && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
            onClick={() => setUploadDialogOpen(true)}
          >
            <Plus className="h-3 w-3" />
            Upload
          </Button>
        )}
      </div>
      <div className="p-5 space-y-3">
        {hasDocuments ? (
          <DocumentFolderTree
            folders={folders}
            onDocumentClick={handleDocumentClick}
            onDeleteDocument={canDelete ? setDocToDelete : undefined}
          />
        ) : (
          <EmptyState
            icon={<FileText className="h-12 w-12" />}
            title="No Documents"
            description="Upload and manage employee documents"
            action={
              canUpload ? (
                <Button
                  variant="outline"
                  className="mt-4 gap-2 cursor-pointer"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="h-4 w-4" />
                  Upload Document
                </Button>
              ) : undefined
            }
          />
        )}
      </div>

      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        employeeId={employee.id}
        folders={folders}
        onUploadSuccess={async () => {
          // Invalidate and refetch documents
          await queryClient.invalidateQueries({
            queryKey: ['documents', employee.id],
            refetchType: 'active',
          });
          // Also refetch to ensure data is fresh
          await queryClient.refetchQueries({
            queryKey: ['documents', employee.id],
          });
        }}
      />

      <ConfirmDialog
        open={!!docToDelete}
        title={<>Delete &ldquo;{docToDelete?.name ?? ''}&rdquo;?</>}
        description="This action cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={deleteDocument.isPending}
        onOpenChange={(next) => {
          if (!next) setDocToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

